import { Alert, Platform } from 'react-native';
import Clipboard from '@react-native-clipboard/clipboard';
import { ElementRef, useCallback, useRef, useState } from 'react';

import { useDispatch, useSelector } from '@bonanza-social/toolkit/state';
import { login, selectLoginDataState } from '../state/auth/auth.slice';
import {
  StackActions,
  StackNavigationProp,
  useNavigation,
  Screen,
  useTransitionEnd,
  BottomSheet,
  Modal,
} from '@bonanza-social/navigation';
import { AuthNavigatorParamsList } from '../navigators/AuthNavigator/AuthNavigator.types';
import { ApiError } from '@bonanza-social/toolkit/api';
import {
  Box,
  Text,
  Input,
  Button,
  KeyboardSpacer,
  BonanzaLogo,
} from '@bonanza-social/moonlight';
import { hasKey, getKey, useTranslation } from '@bonanza-social/i18n';
import Config from '@bonanza-social/toolkit/config';

import { BiometricsButton, Recaptcha, RecaptchaRef } from '../components';
import { LoginRejectedPayload } from '../state/auth/auth.slice.types';
import { openSocialRegistrationModal } from '@bonanza-social/registration/state';

function LoginScreen() {
  const recaptchaRef = useRef<RecaptchaRef>(null);
  const emailRef = useRef<ElementRef<typeof Input>>(null);
  const passwordRef = useRef<ElementRef<typeof Input>>(null);
  const dispatch = useDispatch();
  const loginDataState = useSelector(selectLoginDataState);
  const navigation =
    useNavigation<StackNavigationProp<AuthNavigatorParamsList>>();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showError, setShowError] = useState(false);
  // const [prefilledEmail, setPrefilledEmail] = useState<string>();
  const [recaptchaLoading, setRecaptchaLoading] = useState(false);
  const [error, setError] = useState('');
  const mfaParams = useRef<LoginRejectedPayload | null>(null);
  const transitionEnded = useTransitionEnd();
  const { t } = useTranslation('auth');

  // Hardcoded to true since we are not checking eligibility
  const isEligible = true;

  const onLoginPress = useCallback(
    async (acceptTc: boolean = false) => {
      if (!email || !password) {
        if (!showError) {
          setShowError(true);
          setError(t('missingFields'));
        }

        return;
      }

      if (error) {
        setError('');
      }

      if (mfaParams.current?.email === email) {
        navigation.navigate('MFAScreen', {
          ...mfaParams.current,
          isEligible,
        });

        return;
      }

      setRecaptchaLoading(true);

      const googleRecaptchaToken = await recaptchaRef.current?.getToken();

      if (
        Platform.OS === 'android' &&
        Config.ENVIRONMENT === 'qa' &&
        googleRecaptchaToken?.length &&
        googleRecaptchaToken.length > 10000
      ) {
        Alert.alert(
          'Big recaptcha body',
          'We detected a big recaptcha string. Please paste the clipboard and send it to a developer.',
        );

        Clipboard.setString(googleRecaptchaToken);
      }

      const result = await dispatch(
        login({
          email,
          password,
          acceptTc,
          isEligible,
          googleRecaptchaToken,
        }),
      );

      setRecaptchaLoading(false);

      if (login.rejected.match(result)) {
        if (result.payload instanceof ApiError) {
          const { errorCode, errorMessages } = result.payload?.data || {};

          // TODO: Replace with proper error code once BE is updated
          if (errorMessages?.[0] === 'Terms and conditions not accepted.') {
            BottomSheet.open({
              namespace: 'auth',
              type: 'TermsAndConditions',
              data: {
                confirm: () => onLoginPress(true),
              },
            });
            return;
          }

          let errorMessage = '';

          if (errorCode && hasKey(`auth.errors.${errorCode}`)) {
            errorMessage = getKey(`auth.errors.${errorCode}`);
          } else if (errorMessages?.[0]) {
            errorMessage = errorMessages?.[0];
          }

          setError(errorMessage);
        } else if (result.payload?.requireTwoFactorVerification) {
          mfaParams.current = result.payload;

          navigation.navigate('MFAScreen', {
            ...result.payload,
            isEligible,
          });
        }
      }
      // Commented out for now since we are not using biometrics
      // if (login.fulfilled.match(result)) {
      //   try {
      //     if (Platform.OS === 'android' && email !== prefilledEmail) {
      //       type BonanzaCredentialsModule = {
      //         savePassword: (u: string, p: string) => Promise<void>;
      //       };
      //       const { BonanzaCredentials } = NativeModules as {
      //         BonanzaCredentials?: BonanzaCredentialsModule;
      //       };
      //       if (BonanzaCredentials) {
      //         dispatch(startActivity());

      //         await BonanzaCredentials.savePassword(email, password);
      //       }
      //     }
      //   } catch {
      //     // no-op
      //   }
      // }
    },
    [
      email,
      password,
      error,
      dispatch,
      isEligible,
      showError,
      t,
      navigation,
      // prefilledEmail,
    ],
  );

  // Comment out for now since we are not using biometrics
  // useEffect(() => {
  //   if (
  //     biometricsDataState === 'pending' ||
  //     biometricsDataState === 'fulfilled'
  //   ) {
  //     return;
  //   }

  //   const tryPrefill = async () => {
  //     try {
  //       if (Platform.OS === 'android') {
  //         type BonanzaCredentialsModule = {
  //           getSavedPassword: () => Promise<{
  //             username: string;
  //             password: string;
  //           }>;
  //         };
  //         const { BonanzaCredentials } = NativeModules as {
  //           BonanzaCredentials?: BonanzaCredentialsModule;
  //         };
  //         if (BonanzaCredentials) {
  //           dispatch(startActivity());

  //           const creds = await BonanzaCredentials.getSavedPassword();
  //           if (creds?.username && creds?.password) {
  //             setEmail(creds.username);
  //             setPassword(creds.password);
  //             setPrefilledEmail(creds.username);
  //             return;
  //           }
  //         }
  //       }
  //     } catch {
  //       // ignore prefill errors
  //     }
  //     emailRef.current?.focus();
  //   };
  //   if (transitionEnded) {
  //     tryPrefill();
  //   }
  // }, [transitionEnded, biometricsDataState, dispatch]);

  const onRegisterPress = useCallback(() => {
    if (Platform.OS === 'web') {
      Modal.close();
      dispatch(openSocialRegistrationModal());
    } else {
      navigation.dispatch(StackActions.replace('RegistrationScreen'));
    }
  }, [navigation, dispatch]);

  const onEmailSubmit = useCallback(() => {
    passwordRef.current?.focus();
  }, []);

  return (
    <Screen paddingBottom="safe-area-inset-bottom">
      <Box flex={1} justifyContent="space-between">
        <Box gap="space-600">
          <BonanzaLogo marginTop="space-300" />
          <Box gap="space-600">
            <Input
              ref={emailRef}
              placeholderKey="email"
              placeholderNS="auth"
              autoComplete="username"
              keyboardType="email-address"
              textContentType="emailAddress"
              autoCapitalize="none"
              autoCorrect={false}
              value={email}
              onChangeText={setEmail}
              enterKeyHint="next"
              onSubmitEditing={onEmailSubmit}
              onFocus={() => setShowError(false)}
              importantForAutofill="yes"
              error={error}
            />
            <Box flexDirection="row" gap="space-300">
              <Input
                ref={passwordRef}
                placeholderKey="password"
                placeholderNS="auth"
                secureTextEntry
                autoComplete="current-password"
                textContentType="password"
                value={password}
                autoCorrect={false}
                autoCapitalize="none"
                onChangeText={setPassword}
                onSubmitEditing={() => onLoginPress()}
                enterKeyHint="go"
                importantForAutofill="yes"
                flex={1}
                error={error}
              />
              <BiometricsButton />
            </Box>
            {error ? (
              <Text color="darkSurfaceError" variant="body-m">
                {error}
              </Text>
            ) : null}
            {Config.RECAPTCHA_KEY && transitionEnded ? (
              <Recaptcha ref={recaptchaRef} />
            ) : null}
          </Box>
        </Box>
        <Box>
          <Box gap="space-600">
            <Button
              size="large"
              locKey="login"
              locNS="auth"
              onPress={() => onLoginPress()}
              disabled={showError}
              loading={loginDataState === 'pending' || recaptchaLoading}
            />
            <Box justifyContent="center" flexDirection="row" gap="space-050">
              <Text
                variant="body-s-semi"
                color="darkSurfacePrimary"
                locNS="auth"
                locKey="newToBonanza"
              />
              <Text
                variant="link-small"
                color="darkSurfacePrimary"
                onPress={onRegisterPress}
                locNS="auth"
                locKey="signup"
              />
            </Box>
          </Box>
          <KeyboardSpacer isInsideSafeAreaView additionalOffset={-30} />
        </Box>
      </Box>
    </Screen>
  );
}

export default LoginScreen;
